/**
 * NFL team reference data.
 *
 * Names, locations and abbreviations only: these are public facts. We
 * deliberately do NOT carry team colours or logos: reproducing club
 * branding is a licensing question, and the product's own palette keeps
 * the UI consistent anyway.
 *
 * No player data lives here. The MVP never names a player, because we
 * have no verified source for rosters (see docs/sports-data/README.md).
 */

/**
 * `speakName` is how the team is referred to inside a sentence, and
 * `speakPlural` says which verb form goes with it. This matters more than
 * it looks: "Bills needs 7 yards" is exactly the kind of clumsy sentence
 * that makes a beginner distrust an explanation.
 *
 * Most teams take the city ("Buffalo needs 7 yards"). Where the city is
 * shared between two clubs, we use the nickname and the plural verb
 * ("the Jets need 7 yards").
 */
const team = (
  teamId,
  location,
  name,
  conference,
  division,
  { speakName, speakPlural = false } = {}
) => ({
  teamId,
  abbreviation: teamId,
  location,
  name,
  fullName: `${location} ${name}`,
  speakName: speakName ?? location,
  speakPlural,
  conference,
  division,
});

const NICKNAME = (name) => ({ speakName: `the ${name}`, speakPlural: true });

export const TEAMS = Object.freeze([
  /* AFC East */
  team("BUF", "Buffalo", "Bills", "AFC", "East"),
  team("MIA", "Miami", "Dolphins", "AFC", "East"),
  team("NE", "New England", "Patriots", "AFC", "East"),
  team("NYJ", "New York", "Jets", "AFC", "East", NICKNAME("Jets")),
  /* AFC North */
  team("BAL", "Baltimore", "Ravens", "AFC", "North"),
  team("CIN", "Cincinnati", "Bengals", "AFC", "North"),
  team("CLE", "Cleveland", "Browns", "AFC", "North"),
  team("PIT", "Pittsburgh", "Steelers", "AFC", "North"),
  /* AFC South */
  team("HOU", "Houston", "Texans", "AFC", "South"),
  team("IND", "Indianapolis", "Colts", "AFC", "South"),
  team("JAX", "Jacksonville", "Jaguars", "AFC", "South"),
  team("TEN", "Tennessee", "Titans", "AFC", "South"),
  /* AFC West */
  team("DEN", "Denver", "Broncos", "AFC", "West"),
  team("KC", "Kansas City", "Chiefs", "AFC", "West"),
  team("LV", "Las Vegas", "Raiders", "AFC", "West"),
  team("LAC", "Los Angeles", "Chargers", "AFC", "West", NICKNAME("Chargers")),
  /* NFC East */
  team("DAL", "Dallas", "Cowboys", "NFC", "East"),
  team("NYG", "New York", "Giants", "NFC", "East", NICKNAME("Giants")),
  team("PHI", "Philadelphia", "Eagles", "NFC", "East"),
  team("WAS", "Washington", "Commanders", "NFC", "East"),
  /* NFC North */
  team("CHI", "Chicago", "Bears", "NFC", "North"),
  team("DET", "Detroit", "Lions", "NFC", "North"),
  team("GB", "Green Bay", "Packers", "NFC", "North"),
  team("MIN", "Minnesota", "Vikings", "NFC", "North"),
  /* NFC South */
  team("ATL", "Atlanta", "Falcons", "NFC", "South"),
  team("CAR", "Carolina", "Panthers", "NFC", "South"),
  team("NO", "New Orleans", "Saints", "NFC", "South"),
  team("TB", "Tampa Bay", "Buccaneers", "NFC", "South"),
  /* NFC West */
  team("ARI", "Arizona", "Cardinals", "NFC", "West"),
  team("LAR", "Los Angeles", "Rams", "NFC", "West", NICKNAME("Rams")),
  team("SF", "San Francisco", "49ers", "NFC", "West"),
  team("SEA", "Seattle", "Seahawks", "NFC", "West"),
]);

export const TEAMS_BY_ID = Object.freeze(
  TEAMS.reduce((acc, t) => {
    acc[t.teamId] = t;
    return acc;
  }, {})
);

export function getTeam(teamId) {
  return TEAMS_BY_ID[teamId] ?? null;
}

/** Never throws: an unknown id degrades to the id itself. */
export function teamAbbrev(teamId) {
  return TEAMS_BY_ID[teamId]?.abbreviation ?? String(teamId ?? "N/A");
}

export function teamName(teamId) {
  return TEAMS_BY_ID[teamId]?.name ?? String(teamId ?? "Unknown");
}

export function teamFullName(teamId) {
  return TEAMS_BY_ID[teamId]?.fullName ?? String(teamId ?? "Unknown team");
}

/** How to refer to the team inside a sentence: "Buffalo", "the Jets". */
export function teamSpeakName(teamId) {
  return TEAMS_BY_ID[teamId]?.speakName ?? String(teamId ?? "the team");
}

/** Pick the verb form that agrees with `teamSpeakName`. */
export function teamVerb(teamId, singular, plural) {
  return TEAMS_BY_ID[teamId]?.speakPlural ? plural : singular;
}
